import { useTranslation } from 'react-i18next';
import { ChevronLeftIcon, ChevronRightIcon, ClockIcon, FlagIcon, LayersIcon } from '@utils/icons';
import statusStyles from '../../SchedulerPage/components/_status.module.css';
import { rungClass } from '../../SchedulerPage/lib/ladder';
import { ChecklistChip } from '@components/ui/ChecklistChip/ChecklistChip';
import { formatDateRange } from '../model/formatDateRange';
import type { IPlacedRow } from './workPlanModel';
import styles from './promiseBar.module.css';

export interface IPromiseBarProps {
  row: IPlacedRow;
  today: string;
}

/** The facts line a Trusstor calendar card carries: the floor, the areas
 *  done of all, the hours the IoT counted — each only when there is one. */
function Facts({ row }: { row: IPlacedRow }) {
  const { t } = useTranslation();
  const { floor, moreFloors, areasDone, areasTotal, workHours } = row.facts;
  return (
    <span className={styles.facts}>
      {floor && (
        <span className={styles.fact}>
          {floor}
          {moreFloors > 0 && ` +${moreFloors}`}
        </span>
      )}
      {areasTotal > 0 && (
        <span className={styles.fact}>
          <LayersIcon size={11} />
          {`${areasDone}/${areasTotal}`}
        </span>
      )}
      {workHours > 0 && (
        <span className={styles.fact}>
          <ClockIcon size={11} />
          {t('activities.workPlan.bar.hours', { hours: workHours })}
        </span>
      )}
      <ChecklistChip checklist={row.task.checklist} />
    </span>
  );
}

/**
 * A promise's marks on its day track, a Trusstor calendar card in the Gantt's
 * bar recipe: the planned span toned by its rung — the name (flagged when
 * overdue) over its facts line; no progress band, the rail's PROGRESS column
 * holds the number — and, for an overdue
 * promise, the overrun in its red from the day after its planned end to today.
 */
export function PromiseBar({ row, today }: IPromiseBarProps) {
  const { t, i18n } = useTranslation();
  const { task, rung, placement } = row;
  const { bar, overrun } = placement;
  const late = t('activities.workPlan.overrun', { days: row.lateDays });
  return (
    <>
      {bar && (
        <div
          className={`${styles.bar} ${statusStyles[rungClass(rung)]}`}
          data-rung={rung}
          data-cut-left={bar.cutL || undefined}
          data-cut-right={bar.cutR || undefined}
          style={{ gridColumn: `${bar.col} / span ${bar.span}` }}
        >
          <span className={styles.body}>
            <span className={styles.line}>
              {bar.cutL && <ChevronLeftIcon size={12} />}
              {rung === 'overdue' && (
                <span className={styles.flag}>
                  <FlagIcon size={12} />
                </span>
              )}
              <span className={styles.name}>{task.name}</span>
              {bar.cutR && <ChevronRightIcon size={12} />}
              {overrun && <b className={styles.late}>{late}</b>}
            </span>
            <Facts row={row} />
          </span>
        </div>
      )}
      {overrun && (
        <div
          className={`${styles.overrun} ${statusStyles[rungClass('overdue')]}`}
          data-rung="overdue"
          data-lone={overrun.lone || undefined}
          style={{ gridColumn: `${overrun.col} / span ${overrun.span}` }}
        >
          {/* The planned span is off the window: the overrun carries the card. */}
          {overrun.lone && (
            <span className={styles.body}>
              <span className={styles.line}>
                <ChevronLeftIcon size={12} />
                <span className={styles.flag}>
                  <FlagIcon size={12} />
                </span>
                <span className={styles.name}>
                  {t('activities.workPlan.overrunLabel', {
                    name: task.name,
                    date: formatDateRange(task.end, task.end, i18n.language, Number(today.slice(0, 4))),
                  })}
                </span>
                <b className={styles.late}>{late}</b>
              </span>
              <Facts row={row} />
            </span>
          )}
        </div>
      )}
    </>
  );
}
